import { spotifyService, SpotifyTrack, SpotifyArtist } from './spotifyService';
import { supabase } from '../integrations/supabase/client';

export interface EnhancedGenreDetection {
  genreId: string;
  genreName: string;
  genreSlug: string;
  confidence: number;
  source: 'keyword' | 'spotify_api' | 'fallback';
  alternatives?: Array<{
    genreId: string;
    genreName: string;
    confidence: number;
  }>;
}

export interface GenreSuggestion {
  id: string;
  name: string;
  slug: string;
  category: string;
  iconName: string;
  confidence: number;
  reason: string;
}

class EnhancedSpotifyService {
  /**
   * Enhanced genre detection that uses multiple strategies
   */
  async detectGenre(track: SpotifyTrack): Promise<EnhancedGenreDetection | null> {
    console.log('🔍 ENHANCED SPOTIFY SERVICE - detectGenre called');
    console.log('Track:', track.name, 'by', track.artists[0]?.name);
    
    try {
      // Special-case: Disney detection first
      console.log('🎭 Checking if track is Disney...');
      if (this.isDisneyTrack(track)) {
        console.log('✅ Track identified as Disney');
        const disney = await this.findGenreByName('disney');
        if (disney) {
          console.log('🎭 Disney genre found:', disney);
          return {
            genreId: disney.id,
            genreName: disney.name,
            genreSlug: disney.slug,
            confidence: 0.95,
            source: 'keyword',
            alternatives: await this.getAlternativeGenres(disney.category, 3)
          };
        }
      } else {
        console.log('❌ Track is not Disney');
      }

      // Strategy 1: Keyword-based detection with database lookup
      console.log('🔤 Strategy 1: Keyword-based detection...');
      const keywordResult = await this.detectGenreByKeywords(track);
      console.log('🔤 Keyword result:', keywordResult);
      if (keywordResult && keywordResult.confidence >= 0.7) {
        console.log('✅ Keyword detection successful with high confidence');
        return keywordResult;
      }

      // Strategy 2: Spotify API genre detection
      console.log('🎵 Strategy 2: Spotify API genre detection...');
      const spotifyResult = await this.detectGenreBySpotifyAPI(track);
      console.log('🎵 Spotify API result:', spotifyResult);
      if (spotifyResult && spotifyResult.confidence >= 0.6) {
        console.log('✅ Spotify API detection successful');
        return spotifyResult;
      }

      // Strategy 3: Fallback to keyword detection with lower confidence
      console.log('🔄 Strategy 3: Fallback to keyword detection...');
      if (keywordResult) {
        console.log('✅ Using keyword result as fallback');
        return keywordResult;
      }

      console.log('❌ All strategies failed - returning null');
      return null;
    } catch (error) {
      console.error('💥 Error in enhanced genre detection:', error);
      console.error('Error stack:', error.stack);
      return null;
    }
  }

  /**
   * Detect genre using keyword analysis and database lookup
   */
  private async detectGenreByKeywords(track: SpotifyTrack): Promise<EnhancedGenreDetection | null> {
    const title = track.name.toLowerCase();
    const artist = track.artists[0]?.name.toLowerCase() || '';
    const album = track.album.name.toLowerCase();

    // Disney keyword or curated artist/label match
    if (this.isDisneyTrack(track)) {
      const disney = await this.findGenreByName('disney');
      if (disney) {
        return {
          genreId: disney.id,
          genreName: disney.name,
          genreSlug: disney.slug,
          confidence: 0.95,
          source: 'keyword',
          alternatives: await this.getAlternativeGenres(disney.category, 3)
        };
      }
    }

    // Enhanced keyword patterns with confidence scores
    const keywordPatterns = [
      // Christmas/Holiday (high confidence)
      {
        patterns: ['christmas', 'xmas', 'holiday', 'santa', 'snow', 'winter wonderland'],
        genreKeywords: ['christmas', 'holiday'],
        confidence: 0.9
      },
      // Rock (medium-high confidence)
      {
        patterns: ['rock', 'metal', 'hard rock', 'classic rock'],
        genreKeywords: ['rock', 'metal'],
        confidence: 0.8
      },
      // Country (medium-high confidence)
      {
        patterns: ['country', 'western', 'bluegrass', 'honky tonk'],
        genreKeywords: ['country', 'western'],
        confidence: 0.8
      },
      // Pop (medium confidence)
      {
        patterns: ['pop', 'dance', 'electronic'],
        genreKeywords: ['pop', 'dance'],
        confidence: 0.7
      },
      // Classical (high confidence)
      {
        patterns: ['classical', 'orchestra', 'symphony', 'concerto'],
        genreKeywords: ['classical', 'orchestral'],
        confidence: 0.9
      },
      // Jazz (medium-high confidence)
      {
        patterns: ['jazz', 'swing', 'bebop', 'smooth jazz'],
        genreKeywords: ['jazz'],
        confidence: 0.8
      },
      // Children's (high confidence)
      {
        patterns: ['children', 'kids', 'nursery', 'lullaby'],
        genreKeywords: ['children', 'kids'],
        confidence: 0.9
      },
      // Christian/Gospel (medium-high confidence)
      // FIXED: Only match in title/album, NOT artist name to avoid false positives
      // e.g., artist name "Christian Lee" shouldn't trigger Christian genre
      {
        patterns: ['gospel', 'worship', 'praise', 'christian music', 'contemporary christian'],
        genreKeywords: ['gospel', 'christian'],
        confidence: 0.8,
        titleAlbumOnly: true // Only check title and album, not artist name
      }
    ];

    // Check each pattern
    for (const pattern of keywordPatterns) {
      // For patterns with titleAlbumOnly flag, don't check artist name
      const hasMatch = pattern.patterns.some(keyword => {
        if (pattern.titleAlbumOnly) {
          return title.includes(keyword) || album.includes(keyword);
        }
        return title.includes(keyword) || artist.includes(keyword) || album.includes(keyword);
      });

      if (hasMatch) {
        // Try to find matching genre in database
        for (const genreKeyword of pattern.genreKeywords) {
          const genreResult = await this.findGenreByName(genreKeyword);
          if (genreResult) {
            return {
              genreId: genreResult.id,
              genreName: genreResult.name,
              genreSlug: genreResult.slug,
              confidence: pattern.confidence,
              source: 'keyword',
              alternatives: await this.getAlternativeGenres(genreResult.category, 3)
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Detect genre using Spotify API artist genres
   */
  private async detectGenreBySpotifyAPI(track: SpotifyTrack): Promise<EnhancedGenreDetection | null> {
    console.log('🎵 SPOTIFY API DETECTION - Starting...');
    try {
      // Prefer early Disney detection using names/curated list
      if (this.isDisneyTrack(track)) {
        console.log('🎭 Disney track detected in Spotify API method');
        const disney = await this.findGenreByName('disney');
        if (disney) {
          return {
            genreId: disney.id,
            genreName: disney.name,
            genreSlug: disney.slug,
            confidence: 0.9,
            source: 'spotify_api',
            alternatives: await this.getAlternativeGenres(disney.category, 3)
          };
        }
      }

      // Get artist details from Spotify API
      const artistId = track.artists[0]?.id;
      console.log('🎤 Artist ID:', artistId);
      if (!artistId) {
        console.log('❌ No artist ID found');
        return null;
      }

      console.log('📡 Calling Spotify API for artist details...');
      const artist = await spotifyService.getArtist(artistId);
      console.log('📡 Spotify API response:', artist);
      
      if (!artist) {
        console.log('❌ No artist data returned from Spotify API');
        return null;
      }
      
      if (!artist.genres || artist.genres.length === 0) {
        console.log('❌ No genres found in artist data:', artist.genres);
        return null;
      }
      
      console.log('🎵 Artist genres from Spotify:', artist.genres);

      // Try to map Spotify genres to our database genres
      console.log('🗺️ Mapping Spotify genres to database...');
      const mappedGenre = await this.mapSpotifyGenresToDatabase(artist.genres);
      console.log('🗺️ Mapped genre result:', mappedGenre);
      
      if (mappedGenre) {
        console.log('✅ Successfully mapped Spotify genres to database');
        return {
          genreId: mappedGenre.id,
          genreName: mappedGenre.name,
          genreSlug: mappedGenre.slug,
          confidence: 0.7, // Spotify API is generally reliable
          source: 'spotify_api',
          alternatives: await this.getAlternativeGenres(mappedGenre.category, 3)
        };
      }

      console.log('❌ Failed to map Spotify genres to database');
      return null;
    } catch (error) {
      console.error('💥 Error detecting genre via Spotify API:', error);
      console.error('Error stack:', error.stack);
      return null;
    }
  }

  /**
   * Detect if a track is Disney-related based on names or curated artists/labels
   */
  private isDisneyTrack(track: SpotifyTrack): boolean {
    const title = (track.name || '').toLowerCase();
    const album = (track.album?.name || '').toLowerCase();
    const primaryArtist = (track.artists?.[0]?.name || '').toLowerCase();

    const DISNEY_KEYWORDS = ['disney', 'walt disney'];
    if (DISNEY_KEYWORDS.some(k => title.includes(k) || album.includes(k) || primaryArtist.includes(k))) {
      return true;
    }

    // Curated Disney-associated artists (seed; expandable)
    const DISNEY_ARTISTS = [
      'jordan fisher',
      'idina menzel',
      'lea salonga',
      'christina aguilera',
      'darren criss',
      'michael bolton',
      'kris allen',
      'mandy moore',
      'zac efron',
      'vanessa hudgens',
      'demi lovato',
      'selena gomez',
      'miley cyrus',
      'jonas brothers',
      'sabrina carpenter'
    ];
    if (DISNEY_ARTISTS.includes(primaryArtist)) return true;

    return false;
  }

  /**
   * Map Spotify genres to database genres with real-time creation support
   * FIXED: Prioritize exact matches over partial matches to avoid "k-pop" matching "pop"
   */
  private async mapSpotifyGenresToDatabase(spotifyGenres: string[]): Promise<{ id: string; name: string; category: string } | null> {
    try {
      // Normalize incoming names once
      const lower = (v: string) => v.toLowerCase();
      const incoming = spotifyGenres.map(lower);

      // PRIORITY 1: Try spotify_genre_map FIRST (exact matches from our mapping table)
      // This ensures "k-pop" maps to "k-pop" instead of accidentally matching "pop"
      for (const g of incoming) {
        const { data: mapRow } = await supabase
          .from('spotify_genre_map')
          .select('canonical_slug')
          .eq('raw_genre', g)
          .maybeSingle();
        if (mapRow?.canonical_slug) {
          const { data: can } = await supabase
            .from('genres_canonical')
            .select('id, name, category')
            .eq('slug', mapRow.canonical_slug)
            .maybeSingle();
          if (can) return { id: can.id, name: can.name, category: can.category };
        }
      }

      // PRIORITY 2: Try exact canonical slug match (e.g., "k-pop" -> "k-pop")
      for (const g of incoming) {
        const { data: canBySlug } = await supabase
          .from('genres_canonical')
          .select('id, name, category, slug')
          .eq('slug', g.replace(/\s+/g, '-'))
          .maybeSingle();
        if (canBySlug) {
          return { id: canBySlug.id, name: canBySlug.name, category: canBySlug.category };
        }
      }

      // PRIORITY 3: Try exact name match (case-insensitive but EXACT)
      for (const g of incoming) {
        const { data: canByName } = await supabase
          .from('genres_canonical')
          .select('id, name, category')
          .ilike('name', g) // EXACT match, not %wildcard%
          .limit(1)
          .maybeSingle();
        if (canByName) {
          return { id: canByName.id, name: canByName.name, category: canByName.category };
        }
      }

      // PRIORITY 4: Only if no exact matches, try partial matching (fallback)
      // This prevents "k-pop" from matching "pop" unless there's no exact match
      for (const g of incoming) {
        const { data: canByPartial } = await supabase
          .from('genres_canonical')
          .select('id, name, category')
          .ilike('name', `%${g}%`)
          .limit(1)
          .maybeSingle();
        if (canByPartial) {
          // Double-check: Only use partial match if it's a reasonable substring
          // e.g., "rock" can match "hard rock", but "pop" shouldn't match "k-pop"
          const genreName = canByPartial.name.toLowerCase();
          // If the Spotify genre is a complete word in the database genre name, it's valid
          const genreWords = genreName.split(/[\s-]+/);
          if (genreWords.includes(g)) {
            return { id: canByPartial.id, name: canByPartial.name, category: canByPartial.category };
          }
        }
      }

      // 3) Fall back to legacy genres table (active ones preferred)
      for (const g of incoming) {
        const { data: gen } = await supabase
          .from('genres')
          .select('id, name, category')
          .ilike('name', `%${g}%`)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();
        if (gen) return { id: gen.id, name: gen.name, category: gen.category };
      }

      // 4) NEW: Create missing genres in real-time
      try {
        console.log('No existing genres found, creating new genres from Spotify:', spotifyGenres);
        const createdGenres = await this.createGenresFromSpotifyGenres(spotifyGenres);
        
        if (createdGenres.length > 0) {
          // Return the first created genre (prioritize by Spotify order)
          const firstGenre = createdGenres[0];
          return { 
            id: firstGenre.id, 
            name: firstGenre.name, 
            category: firstGenre.category 
          };
        }
      } catch (createError) {
        console.error('Error creating genres from Spotify:', createError);
      }

      return null;
    } catch (error) {
      console.error('Error mapping Spotify genres to database:', error);
      return null;
    }
  }

  /**
   * Find genre by name in the database
   */
  private async findGenreByName(name: string): Promise<{ id: string; name: string; category: string } | null> {
    try {
      const { data: genre, error } = await supabase
        .from('genres')
        .select('id, name, category')
        .ilike('name', `%${name}%`)
        .eq('is_active', true)
        .order('sequence_count', { ascending: false })
        .limit(1)
        .single();

      if (error || !genre) return null;

      return {
        id: genre.id,
        name: genre.name,
        category: genre.category
      };
    } catch (error) {
      console.error('Error finding genre by name:', error);
      return null;
    }
  }

  /**
   * Get alternative genres from the same category
   */
  private async getAlternativeGenres(category: string, limit: number = 3): Promise<Array<{ genreId: string; genreName: string; confidence: number }>> {
    try {
      // Case-insensitive category match to avoid missing peers due to capitalization
      const { data: genres, error } = await supabase
        .from('genres')
        .select('id, name, sequence_count, category')
        .ilike('category', category)
        .eq('is_active', true)
        .order('sequence_count', { ascending: false })
        .limit(limit);

      let results: Array<{ genreId: string; genreName: string; confidence: number }> = [];
      if (!error && Array.isArray(genres) && genres.length > 0) {
        results = genres.map(genre => ({
          genreId: genre.id,
          genreName: genre.name,
          confidence: 0.5
        }));
      }

      // Fallback: if no peers found in same category, use top active genres by sequence_count
      if (results.length === 0) {
        const { data: popular } = await supabase
          .from('genres')
          .select('id, name, sequence_count')
          .eq('is_active', true)
          .order('sequence_count', { ascending: false })
          .limit(limit);
        if (Array.isArray(popular)) {
          results = popular.map(p => ({ genreId: p.id, genreName: p.name, confidence: 0.4 }));
        }
      }

      return results;
    } catch (error) {
      console.error('Error getting alternative genres:', error);
      return [];
    }
  }

  /**
   * Create a new genre from Spotify genre using the API endpoint with service role permissions
   */
  private async createGenreFromSpotifyGenre(spotifyGenre: string): Promise<string | null> {
    try {
      const raw = spotifyGenre.toLowerCase();
      
      // First, check if mapping already exists to avoid unnecessary API calls
      const { data: mapRow } = await supabase
        .from('spotify_genre_map')
        .select('canonical_slug')
        .eq('raw_genre', raw)
        .maybeSingle();

      if (mapRow?.canonical_slug) {
        const { data: can } = await supabase
          .from('genres_canonical')
          .select('id')
          .eq('slug', mapRow.canonical_slug)
          .maybeSingle();
        if (can?.id) return can.id;
      }

      // Try to find active legacy genre by name
      const { data: gen } = await supabase
        .from('genres')
        .select('id')
        .ilike('name', `%${raw}%`)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (gen?.id) return gen.id;

      // If genre doesn't exist, create it via API endpoint
      try {
        const response = await fetch('/api/genres/create-from-spotify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            spotifyGenres: [spotifyGenre],
            trackContext: {} // Can be enhanced later with track info
          })
        });

        if (!response.ok) {
          console.error('Failed to create genre via API:', response.statusText);
          return null;
        }

        const result = await response.json();
        
        if (result.success && result.genres?.length > 0) {
          const createdGenre = result.genres[0];
          console.log(`Successfully created/found genre: ${createdGenre.name} (${createdGenre.source})`);
          return createdGenre.id;
        }

        return null;
      } catch (apiError) {
        console.error('Error calling genre creation API:', apiError);
        return null;
      }
    } catch (error) {
      console.error('Error creating/mapping genre from Spotify genre:', error);
      return null;
    }
  }

  /**
   * Create multiple genres from Spotify genres array
   */
  private async createGenresFromSpotifyGenres(spotifyGenres: string[]): Promise<Array<{ id: string; name: string; slug: string; category: string }>> {
    if (!spotifyGenres || spotifyGenres.length === 0) return [];

    try {
      const response = await fetch('/api/genres/create-from-spotify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          spotifyGenres,
          trackContext: {} // Can be enhanced later with track info
        })
      });

      if (!response.ok) {
        console.error('Failed to create genres via API:', response.statusText);
        return [];
      }

      const result = await response.json();
      
      if (result.success && result.genres?.length > 0) {
        console.log(`Successfully processed ${result.totalProcessed} genres: ${result.totalCreated} created, ${result.totalFound} found`);
        return result.genres;
      }

      return [];
    } catch (apiError) {
      console.error('Error calling bulk genre creation API:', apiError);
      return [];
    }
  }

  /**
   * Get genre suggestions for a track with enhanced Spotify genre creation
   */
  async getGenreSuggestions(track: SpotifyTrack): Promise<GenreSuggestion[]> {
    try {
      const suggestions: GenreSuggestion[] = [];

      // Enhanced approach: Try to get Spotify artist genres first for more accurate suggestions
      let spotifyGenres: string[] = [];
      try {
        const artistId = track.artists[0]?.id;
        if (artistId) {
          const artist = await spotifyService.getArtist(artistId);
          if (artist?.genres && artist.genres.length > 0) {
            spotifyGenres = artist.genres;
            console.log('Found Spotify genres for artist:', spotifyGenres);
            
            // Create/find genres from Spotify API in real-time
            const createdGenres = await this.createGenresFromSpotifyGenres(spotifyGenres);
            
            if (createdGenres.length > 0) {
              createdGenres.forEach((genre, index) => {
                suggestions.push({
                  id: genre.id,
                  name: genre.name,
                  slug: genre.slug,
                  category: genre.category,
                  iconName: 'Music',
                  confidence: index === 0 ? 0.9 : Math.max(0.6, 0.9 - (index * 0.1)), // Higher confidence for first genres
                  reason: `From Spotify (${genre.source === 'created' ? 'new' : 'existing'})`
                });
              });
            }
          }
        }
      } catch (spotifyError) {
        console.error('Error getting Spotify artist genres:', spotifyError);
      }

      // Fallback to traditional detection if no Spotify genres found
      if (suggestions.length === 0) {
        const detection = await this.detectGenre(track);
        if (detection) {
          // Add the detected genre as the primary suggestion
          suggestions.push({
            id: detection.genreId,
            name: detection.genreName,
            slug: detection.genreName.toLowerCase().replace(/\s+/g, '-'),
            category: 'Detected',
            iconName: 'Music',
            confidence: detection.confidence,
            reason: `Detected from ${detection.source}`
          });

          // Add alternatives
          if (detection.alternatives) {
            detection.alternatives.forEach(alt => {
              suggestions.push({
                id: alt.genreId,
                name: alt.genreName,
                slug: alt.genreName.toLowerCase().replace(/\s+/g, '-'),
                category: 'Alternative',
                iconName: 'Music',
                confidence: alt.confidence,
                reason: 'Related genre'
              });
            });
          }
        }
      }

      // Always add some popular genres as additional suggestions (but avoid duplicates)
      const popularGenres = await this.getPopularGenres(3);
      popularGenres.forEach(genre => {
        if (!suggestions.find(s => s.id === genre.id)) {
          suggestions.push({
            ...genre,
            reason: 'Popular genre'
          });
        }
      });

      // If still no suggestions, return popular genres
      return suggestions.length > 0 ? suggestions : await this.getPopularGenres(5);
      
    } catch (error) {
      console.error('Error getting genre suggestions:', error);
      return await this.getPopularGenres(5);
    }
  }

  /**
   * Get popular genres from the database
   */
  async getPopularGenres(limit: number = 5): Promise<GenreSuggestion[]> {
    try {
      const { data: genres, error } = await supabase
        .from('genres')
        .select('id, name, category, sequence_count')
        .eq('is_active', true)
        .order('sequence_count', { ascending: false })
        .limit(limit);

      if (error || !genres) return [];

      return genres.map(genre => ({
        id: genre.id,
        name: genre.name,
        slug: genre.name.toLowerCase().replace(/\s+/g, '-'),
        category: genre.category,
        iconName: 'Music',
        confidence: 0.3,
        reason: 'Popular genre'
      }));
    } catch (error) {
      console.error('Error getting popular genres:', error);
      return [];
    }
  }

  /**
   * Validate if a genre ID exists and is active
   */
  async validateGenre(genreId: string): Promise<boolean> {
    try {
      const { data: genre, error } = await supabase
        .from('genres')
        .select('id')
        .eq('id', genreId)
        .eq('is_active', true)
        .single();

      return !error && !!genre;
    } catch (error) {
      console.error('Error validating genre:', error);
      return false;
    }
  }

  /**
   * Get the supabase client for direct access
   */
  get supabase() {
    return supabase;
  }
}

// Create singleton instance
const enhancedSpotifyService = new EnhancedSpotifyService();

export { enhancedSpotifyService };
export default enhancedSpotifyService; 